import { useCallback } from 'react';
import { useMount } from '@simplywallst/ui-core';

import {
  createTickerCell,
  getTickerCellData,
} from '../../components/CustomCell/Ticker';

import type {
  TickerCell,
  TickerCellData,
} from '../../components/CustomCell/Ticker/types';
import type {
  CustomCellProps as Props,
  CustomCellEditor,
} from '../../components/CustomCell/types';

/**
 * Allow the component to settle and only call onFinishedEditing onMount
 *
 * Fixes:
 *
 * Warning: Cannot update a component (`ForwardRef(DataEditorImpl)`) while
 * rendering a different component (`Editor`). To locate the bad setState()
 * call inside `Editor`, follow the stack trace as described in
 * https://reactjs.org/link/setstate-in-render Error Component Stack
 */
const DumbRender = ({ onMount }: { onMount: () => void }) => {
  useMount(() => onMount());
  return null;
};

const Editor: CustomCellEditor<TickerCell> = ({
  value,
  onFinishedEditing,
}: Props<TickerCell>) => {
  return <DumbRender onMount={() => onFinishedEditing(value)} />;
};

const withTickerData = <P,>(
  WrappedEditor: React.ComponentType<P>,
  tickerData: Partial<TickerCellData>
) => {
  const EditorHOC = ({
    onFinishedEditing,
    ...restProps
  }: Omit<P, 'onFinishedEditing'> & {
    onFinishedEditing: (cell: TickerCell) => void;
  }) => {
    // Intercept the original onFinishedEditing:
    const handleFinishedEditing = useCallback(
      (updatedCell: TickerCell) => {
        // Inject the ticker data into the cell and call the original onFinishedEditing
        const newCell = {
          ...updatedCell,
          data: getTickerCellData({
            ...updatedCell.data,
            ...tickerData,
          }),
        };
        onFinishedEditing(newCell);
      },
      [onFinishedEditing]
    );

    return (
      <WrappedEditor
        {...(restProps as P)}
        onFinishedEditing={handleFinishedEditing}
      />
    );
  };

  return EditorHOC;
};

export const createTickerCellRenderer = (tickerData: Partial<TickerCellData>) =>
  createTickerCell({
    provideEditor: () => ({
      editor: withTickerData(Editor, tickerData),
      disablePadding: true,
    }),
    onPaste: (_, cell) => {
      return {
        ...cell,
        ...getTickerCellData({
          ...cell,
          ...tickerData,
        }),
      };
    },
  });
